<script>
  import { tick } from "svelte";
  import { fetchWithSession } from "../../../../lib/util/fetchWithSession";
  import Button from "lib/components/Button.svelte";
  import Modal from "lib/components/Modal.svelte";
  import { Loading } from "carbon-components-svelte";
  import BpmnInlinePreview from "./BpmnInlinePreview.svelte";
  import Toggle from "@components/Toggle.svelte";

  export let idproject = "";
  export let currentSubtask = "";
  export let currentSubtaskEntry = null;
  export let activitiesToShow = [];
  export let edgesToShow = [];
  export let bpmnActivityRows = [];
  export let getProcessArtifacts = () => [];
  export let pddGenerating = false;
  export let open = false;

  let pddBpmnRendererElement;
  let pddBpmnXml = "";
  let pddDownloadModalOpen = false;
  let pddDownloadGenerator = "alteryx";
  // Das PDD ist immer v2 (SOLL-Ablauf); ohne Schrittbilder fehlen nur Bilder und Werte (02.10.: v1 kam als "altes PDD").
  // Offene Fragen sind eingeklappt - beantworten geht jetzt oder spaeter, das PDD listet sie solange als offen.
  let questionsOpen = false;
  // Screenshots im PDD v2 und in der zweiten Aufnahmerunde: aus = sie bleiben auf dem PC.
  let pddScreenshots = true;
  let pddDownloadElapsedSeconds = 0;
  let pddDownloadElapsedTimer;
  let pddDownloadStep = 0;
  let pddDownloadTotal = 0;
  let pddDownloadStepLabel = "";
  let pddDownloadAbortController;
  let generatedPddFilename = "";
  let pddDownloadError = "";
  let pddDownloadCancelled = false;
  let n8nPrepareModalOpen = false;
  let n8nPrepareElapsedSeconds = 0;
  let n8nPrepareElapsedTimer;
  let n8nPrepareStep = 0;
  let n8nPrepareTotal = 0;
  let n8nPrepareStepLabel = "";
  let n8nPrepareAbortController;
  let preparedWorkflowName = "";
  let preparedWorkflowEditorUrl = "";
  let n8nPrepareError = "";
  let n8nPrepareCancelled = false;

  // PDD v2: offene Fragen aus den Aufnahmen - was sie nicht zeigen konnten (Herkunft eines Werts, eine Grenze, eine
  // unerklaerte Variante). Die Antwort entscheidet beim naechsten Erzeugen; bisher stand sie hoechstens in einer Mail.
  let clarifications = { open: [], answered: [] };
  let clarificationsLoading = false;
  let clarificationsError = "";
  let clarificationsLoadedFor = "";
  let chosenAnswer = {};
  let answerText = {};
  let savingAnswer = "";

  $: if (open && currentSubtask && clarificationsLoadedFor !== currentSubtask) {
    loadClarifications();
  }

  function needsText(answer) {
    return /bitte nennen|woher sonst|anderen System/.test(answer || "");
  }

  async function loadClarifications() {
    clarificationsLoadedFor = currentSubtask;
    clarificationsLoading = true;
    clarificationsError = "";
    try {
      const params = new URLSearchParams({
        idproject,
        subtask_id: currentSubtask,
        task_name: currentSubtaskEntry?.name || "",
      });
      const response = await fetchWithSession(
        `/api/damprojects/task-pdd/clarifications?${params}`,
      );
      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Could not load the open questions."),
        );
      }
      const data = await response.json();
      clarifications = { open: data.open || [], answered: data.answered || [] };
    } catch (error) {
      clarificationsError = error?.message || "Could not load the open questions.";
    } finally {
      clarificationsLoading = false;
    }
  }

  async function saveAnswer(question, answer, value = "") {
    savingAnswer = question.key;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/task-pdd/clarifications`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subtask_id: currentSubtask,
            question_key: question.key,
            question: question.question,
            answer,
            value,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Could not save the answer."));
      }
      await loadClarifications();
    } catch (error) {
      clarificationsError = error?.message || "Could not save the answer.";
    } finally {
      savingAnswer = "";
    }
  }

  // Der Server antwortet erst am Ende mit dem fertigen Ergebnis - gemessen
  // nach ueber zehn Minuten. Waehrenddessen fragen wir hier nach, welcher
  // Schritt gerade laeuft, damit im Modal nicht nur eine Zahl steigt.
  function trackGenerationProgress(runId, onProgress) {
    const timer = setInterval(async () => {
      try {
        const response = await fetchWithSession(
          `/api/damprojects/task-pdd/progress/${runId}`,
        );
        if (!response.ok) return;
        const data = await response.json();
        if (data?.result === "success") onProgress(data);
      } catch (error) {
        // Ein verpasster Abruf ist kein Fehler: der naechste kommt in einer Sekunde.
      }
    }, 1000);
    return () => clearInterval(timer);
  }

  function pddFilename(name, generator = "alteryx") {
    const value = String(name || "").trim();
    const underscored = value
      .replace(/\s+/g, "_")
      .replace(/[^A-Za-z0-9_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
    const label = generator === "n8n" ? "n8n" : "Alteryx";
    return `${underscored || "task"}_PDD_(${label}).pdf`;
  }

  function sanitizeDownloadFilename(filename, fallbackName) {
    const candidate = String(filename || "").trim();
    if (!candidate) return fallbackName;

    const sanitized = candidate
      .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");

    return !sanitized || sanitized === "." || sanitized === ".."
      ? fallbackName
      : sanitized;
  }

  function getDownloadFilename(response, fallbackName) {
    const disposition = response.headers.get("content-disposition") || "";
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return fallbackName;
      }
    }
    const plainMatch = disposition.match(/filename="?([^"]+)"?/i);
    return plainMatch?.[1] || fallbackName;
  }

  async function getErrorMessage(response, fallbackMessage) {
    const rawText = await response.text();
    if (!rawText) return `${fallbackMessage} (HTTP ${response.status})`;

    try {
      return JSON.parse(rawText).message || fallbackMessage;
    } catch {
      return `${fallbackMessage} (HTTP ${response.status})`;
    }
  }

  function getVisibleBpmnEdges() {
    return (Array.isArray(edgesToShow) ? edgesToShow : [])
      .map((edge) => ({
        from: String(edge?.from || edge?.source || "").trim(),
        to: String(edge?.to || edge?.target || "").trim(),
        value: Number(edge?.value ?? edge?.weight ?? edge?.data?.value ?? 1) || 1,
      }))
      .filter((edge) => edge.from && edge.to && edge.from !== edge.to);
  }

  function getVisibleTaskNodes() {
    return (Array.isArray(activitiesToShow) ? activitiesToShow : [])
      .map((node) => ({
        id: String(node?.id || "").trim(),
        label: String(node?.data?.label || node?.label || "").trim(),
        parent_id: String(node?.parentId || node?.parentNode || "").trim(),
        type: String(node?.type || "").trim(),
      }))
      .filter((node) => node.id || node.label);
  }

  function buildPddBpmnRequestBody(visibleEdges, visibleNodes, task) {
    const visibleActivityLabels = new Set(
      visibleNodes
        .map((node) => node.label || node.id)
        .filter((label) => label && label !== "Start" && label !== "End"),
    );
    const visibleActivities = (bpmnActivityRows || []).filter((activity) =>
      visibleActivityLabels.has(activity.activity),
    );
    const processArtifacts = getProcessArtifacts(visibleActivityLabels);
    const laneByActivity = Object.fromEntries(
      visibleActivities
        .filter((activity) => activity.activity && activity.team)
        .map((activity) => [activity.activity, activity.team]),
    );

    return {
      edges: visibleEdges.map((edge) => [edge.from, edge.to, edge.value]),
      task_name: task.name,
      team_name: task.teams?.[0] || null,
      lane_by_activity: Object.keys(laneByActivity).length
        ? laneByActivity
        : null,
      add_xor_labels_via_llm: false,
      insert_gateways_on_split_and_merge: false,
      process_artifacts: processArtifacts.length ? processArtifacts : null,
      node_notes: null,
      connection_min_frequency: 0,
    };
  }

  // RPA bot for the systems without API: a script built from the recordings (backend/pdd/rpa_bot.py).
  let botLoading = false;
  let botError = "";
  // Second recording round of the task (backend/pdd/kampagne.py): values only for the fields the first round lacks.
  let campaign = null;
  let campaignLoading = false;
  let campaignError = "";

  async function startCampaign() {
    campaignLoading = true;
    campaignError = "";
    try {
      const response = await fetchWithSession("/api/damprojects/task-pdd/campaign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idproject,
          subtask_id: currentSubtask,
          task_name: currentSubtaskEntry?.name || "",
          screenshots: pddScreenshots,
        }),
      });
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Could not start the second recording round."));
      }
      campaign = await response.json();
    } catch (error) {
      campaignError = error?.message || "Could not start the second recording round.";
    } finally {
      campaignLoading = false;
    }
  }

  async function checkCampaign() {
    campaignLoading = true;
    campaignError = "";
    try {
      const response = await fetchWithSession("/api/damprojects/task-pdd/campaign/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subtask_id: currentSubtask }),
      });
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Could not check the second recording round."));
      }
      campaign = await response.json();
    } catch (error) {
      campaignError = error?.message || "Could not check the second recording round.";
    } finally {
      campaignLoading = false;
    }
  }

  $: campaignText = !campaign?.status
    ? ""
    : campaign.status === "fertig"
      ? "Second round complete - PDD and n8n draft are ready."
      : `Second round running: ${campaign.stand?.faelle ?? 0} of ${campaign.auftrag?.faelle ?? "?"} cases, `
        + `${campaign.auftrag?.felder?.length ?? 0} fields, until ${String(campaign.bis || "").slice(0, 10)}.`;

  async function downloadRpaBot() {
    botLoading = true;
    botError = "";
    try {
      const params = new URLSearchParams({
        idproject,
        subtask_id: currentSubtask,
        task_name: currentSubtaskEntry?.name || "",
      });
      const response = await fetchWithSession(`/api/damprojects/task-pdd/rpa-bot?${params}`);
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Could not build the RPA bot."));
      }
      const name = (response.headers.get("Content-Disposition") || "").match(/filename="?([^";]+)"?/);
      downloadBlob(await response.blob(), name ? name[1] : "rpa_bot.ps1", "rpa_bot.ps1");
    } catch (error) {
      botError = error?.message || "Could not build the RPA bot.";
    } finally {
      botLoading = false;
    }
  }

  function downloadBlob(blob, filename, fallbackFilename) {
    const url = URL.createObjectURL(blob);
    const safeFilename = sanitizeDownloadFilename(filename, fallbackFilename);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = safeFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function svgToPngDataUrl(svgText, width = 1600, height = 650) {
    return new Promise((resolve, reject) => {
      const svgBlob = new Blob([svgText], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);
      const image = new Image();
      image.onload = () => {
        const viewBoxMatch = String(svgText).match(/viewBox=["']([^"']+)["']/i);
        const viewBox = viewBoxMatch
          ? viewBoxMatch[1].split(/\s+/).map(Number)
          : [];
        const sourceWidth = Number(viewBox[2]) || Number(image.width) || width;
        const sourceHeight =
          Number(viewBox[3]) || Number(image.height) || height;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = Math.max(
          320,
          Math.round(width / (sourceWidth / Math.max(sourceHeight, 1))),
        );
        const context = canvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/png"));
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to render BPMN SVG."));
      };
      image.src = url;
    });
  }

  async function renderPddBpmnImage(bpmnRequestBody, signal) {
    const response = await fetchWithSession(`/api/damprojects/generateBPMN`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal,
      body: JSON.stringify(bpmnRequestBody),
    });
    if (!response.ok) {
      throw new Error(`BPMN generation failed with status ${response.status}.`);
    }

    pddBpmnXml = await response.text();
    await tick();
    const svg = await pddBpmnRendererElement?.saveSVG();
    if (!svg) throw new Error("BPMN diagram could not be rendered.");
    return svgToPngDataUrl(svg);
  }

  async function startPddDownload(generator = "alteryx") {
    if (!currentSubtask || pddGenerating) return;
    if (!currentSubtaskEntry) {
      pddDownloadError = "No task is selected.";
      pddDownloadModalOpen = true;
      return;
    }

    const task = currentSubtaskEntry;
    const requestController = new AbortController();
    pddDownloadAbortController = requestController;
    pddDownloadGenerator = generator;
    generatedPddFilename = "";
    pddDownloadError = "";
    pddDownloadCancelled = false;
    pddGenerating = true;
    pddDownloadElapsedSeconds = 0;
    pddDownloadStep = 0;
    pddDownloadTotal = 0;
    // Bis der Server den ersten Schritt meldet, baut das Frontend noch selbst
    // am Diagramm - das ist der ehrliche Text fuer diese Zeit.
    pddDownloadStepLabel = "Preparing the task diagram";
    const requestTimer = setInterval(() => {
      pddDownloadElapsedSeconds += 1;
    }, 1000);
    pddDownloadElapsedTimer = requestTimer;
    const runId = crypto.randomUUID();
    const stopProgress = trackGenerationProgress(runId, (data) => {
      pddDownloadStep = data.step;
      pddDownloadTotal = data.total;
      pddDownloadStepLabel = data.label;
    });

    try {
      const visibleBpmnEdges = getVisibleBpmnEdges();
      if (!visibleBpmnEdges.length) {
        throw new Error(
          "No visible BPMN graph is available yet. Please wait until the Task Explorer graph has loaded.",
        );
      }
      const visibleTaskNodes = getVisibleTaskNodes();
      const bpmnRequestBody = buildPddBpmnRequestBody(
        visibleBpmnEdges,
        visibleTaskNodes,
        task,
      );
      const bpmnImage = await renderPddBpmnImage(
        bpmnRequestBody,
        requestController.signal,
      );
      if (requestController.signal.aborted) return;
      const response = await fetchWithSession(`/api/damprojects/task-pdd`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: requestController.signal,
        body: JSON.stringify({
          idproject,
          subtask_id: currentSubtask,
          task_name: task.name,
          visible_bpmn_edges: visibleBpmnEdges,
          visible_task_nodes: visibleTaskNodes,
          bpmn_request_body: bpmnRequestBody,
          bpmn_image: bpmnImage,
          team_name: task.teams?.[0] || null,
          generator,
          output_format: "pdf",
          output_language: "English",
          run_id: runId,
          pdd_version: "2",
          screenshots: pddScreenshots,
        }),
      });

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Could not generate PDD."),
        );
      }

      const blob = await response.blob();
      if (requestController.signal.aborted) return;
      const fallbackFilename = pddFilename(task.name, generator);
      generatedPddFilename = getDownloadFilename(response, fallbackFilename);
      downloadBlob(
        blob,
        generatedPddFilename,
        fallbackFilename,
      );
      // PDD exports download automatically, so there is no follow-up action.
      pddDownloadModalOpen = false;
    } catch (error) {
      if (error?.name === "AbortError" || requestController.signal.aborted) {
        pddDownloadCancelled = true;
        return;
      }
      pddDownloadError = error?.message || "PDD generation failed.";
    } finally {
      clearInterval(requestTimer);
      stopProgress();
      if (pddDownloadElapsedTimer === requestTimer) {
        pddDownloadElapsedTimer = undefined;
      }
      if (pddDownloadAbortController === requestController) {
        pddDownloadAbortController = undefined;
        pddGenerating = false;
      }
    }
  }

  async function choosePddTemplate(generator) {
    open = false;
    pddDownloadGenerator = generator;
    generatedPddFilename = "";
    pddDownloadError = "";
    pddDownloadCancelled = false;
    pddDownloadModalOpen = true;
    await startPddDownload(generator);
  }

  function cancelPddDownload() {
    if (pddDownloadAbortController) {
      pddDownloadAbortController.abort();
    }
    clearInterval(pddDownloadElapsedTimer);
    pddDownloadElapsedTimer = undefined;
    pddDownloadCancelled = true;
    pddGenerating = false;
    pddDownloadModalOpen = false;
  }

  function closePddDownloadModal() {
    if (pddGenerating) cancelPddDownload();
    pddDownloadModalOpen = false;
  }

  export function openN8nPrepareModal() {
    if (!currentSubtask || pddGenerating) return;
    if (!currentSubtaskEntry) {
      n8nPrepareError = "No task is selected.";
      n8nPrepareModalOpen = true;
      return;
    }
    preparedWorkflowName = "";
    preparedWorkflowEditorUrl = "";
    n8nPrepareError = "";
    n8nPrepareCancelled = false;
    n8nPrepareModalOpen = true;
  }

  function openInNewTab(url) {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  function openPreparedWorkflowInN8n() {
    if (preparedWorkflowEditorUrl) openInNewTab(preparedWorkflowEditorUrl);
  }

  function cancelN8nPreparation() {
    if (n8nPrepareAbortController) {
      n8nPrepareAbortController.abort();
    }
    clearInterval(n8nPrepareElapsedTimer);
    n8nPrepareElapsedTimer = undefined;
    n8nPrepareCancelled = true;
    pddGenerating = false;
    n8nPrepareModalOpen = false;
  }

  function closeN8nPrepareModal() {
    if (pddGenerating) {
      cancelN8nPreparation();
      return;
    }
    n8nPrepareModalOpen = false;
  }

  async function prepareN8nWorkflow() {
    if (!currentSubtask || pddGenerating) return;
    if (!currentSubtaskEntry) {
      n8nPrepareError = "No task is selected.";
      return;
    }

    const task = currentSubtaskEntry;
    const requestController = new AbortController();
    n8nPrepareAbortController = requestController;
    n8nPrepareError = "";
    n8nPrepareCancelled = false;
    pddGenerating = true;
    n8nPrepareElapsedSeconds = 0;
    n8nPrepareStep = 0;
    n8nPrepareTotal = 0;
    n8nPrepareStepLabel = "Preparing the task diagram";
    const requestTimer = setInterval(() => {
      n8nPrepareElapsedSeconds += 1;
    }, 1000);
    n8nPrepareElapsedTimer = requestTimer;
    const runId = crypto.randomUUID();
    const stopProgress = trackGenerationProgress(runId, (data) => {
      n8nPrepareStep = data.step;
      n8nPrepareTotal = data.total;
      n8nPrepareStepLabel = data.label;
    });
    try {
      const visibleBpmnEdges = getVisibleBpmnEdges();
      const visibleTaskNodes = getVisibleTaskNodes();
      const bpmnRequestBody = buildPddBpmnRequestBody(
        visibleBpmnEdges,
        visibleTaskNodes,
        task,
      );
      const response = await fetchWithSession(
        `/api/damprojects/task-pdd/n8n-workflow`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: requestController.signal,
          body: JSON.stringify({
            idproject,
            subtask_id: currentSubtask,
            task_name: task.name,
            visible_bpmn_edges: visibleBpmnEdges,
            visible_task_nodes: visibleTaskNodes,
            bpmn_request_body: bpmnRequestBody,
            team_name: task.teams?.[0] || null,
            output_language: "English",
            run_id: runId,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Could not create the n8n workflow draft."),
        );
      }

      const result = await response.json();
      if (requestController.signal.aborted) return;
      preparedWorkflowName = result.workflow_name || "n8n workflow draft";
      preparedWorkflowEditorUrl = result.workflow_editor_url || result.workflow_list_url || "";
    } catch (error) {
      if (error?.name === "AbortError" || requestController.signal.aborted) {
        n8nPrepareCancelled = true;
        return;
      }
      n8nPrepareError =
        error?.message || "Could not create the n8n workflow draft.";
    } finally {
      clearInterval(requestTimer);
      stopProgress();
      if (n8nPrepareElapsedTimer === requestTimer) {
        n8nPrepareElapsedTimer = undefined;
      }
      if (n8nPrepareAbortController === requestController) {
        n8nPrepareAbortController = undefined;
        pddGenerating = false;
      }
    }
  }
</script>

<Modal
  bind:open
  modalHeading="Generate PDD for this task"
  modalClass="pdd-modal-dialog pdd-modal-wide"
  width={800}
  actions
  primaryButtonText="Use n8n (Beta)"
  primaryButtonVariant="secondary"
  primaryButtonNotAllow={pddGenerating}
  primaryButtonAction={() => choosePddTemplate("n8n")}
  secondaryButtonText="Use Alteryx (Beta)"
  secondaryButtonAction={() => choosePddTemplate("alteryx")}
  preventCloseOnClickOutside
  on:close={() => (open = false)}
>
  <div class="pdd-template-copy">
    <p>
      Choose the automation tool used for this task. We’ll generate the Process
      Definition Document (PDD) in the matching format.
    </p>
      <div class="pdd-v2-option">
        <span>Include screenshots (blurred and approved by the recording person)</span>
        <Toggle bind:active={pddScreenshots} />
      </div>
      <div class="pdd-questions">
        <div class="pdd-questions-head">
          <strong>
            Open questions from the recordings{#if !clarificationsLoading && clarifications.open.length}
              <span class="pdd-questions-count">{clarifications.open.length} open</span>{/if}
          </strong>
          {#if clarifications.open.length || clarifications.answered.length}
            <button type="button" class="pdd-link" aria-expanded={questionsOpen} on:click={() => (questionsOpen = !questionsOpen)}>
              {questionsOpen ? "Hide questions" : "Answer now"}
            </button>
          {/if}
        </div>
        <p class="pdd-questions-hint">
          Optional – you can generate the PDD now and answer later. Unanswered questions are listed in the PDD; your
          answers are used the next time it is generated.
        </p>
        {#if clarificationsLoading}
          <div class="pdd-progress" role="status">
            <Loading small withOverlay={false} />
            <span>Analysing the recordings…</span>
          </div>
        {:else if clarificationsError}
          <p class="pdd-status-message error" role="alert">{clarificationsError}</p>
        {:else if !clarifications.open.length && !clarifications.answered.length}
          <p class="pdd-questions-hint">No open questions.</p>
        {:else if questionsOpen}
          {#each clarifications.open as question (question.key)}
            <div class="pdd-question">
              <p class="pdd-question-title"><span class="pdd-question-step">{question.step}</span>{question.question}</p>
              <div class="pdd-question-answers" role="radiogroup" aria-label={question.question}>
                {#each question.answers as answer}
                  <button type="button" role="radio" class="pdd-chip" class:selected={chosenAnswer[question.key] === answer}
                          aria-checked={chosenAnswer[question.key] === answer}
                          on:click={() => (chosenAnswer[question.key] = answer)}>{answer}</button>
                {/each}
              </div>
              {#if needsText(chosenAnswer[question.key])}
                <input class="pdd-question-text" type="text" placeholder="Please specify"
                       bind:value={answerText[question.key]} />
              {/if}
              {#if chosenAnswer[question.key]}
                <div class="pdd-question-save">
                  <Button
                    text={savingAnswer === question.key ? "Saving…" : "Save answer"}
                    size="sm"
                    disabled={savingAnswer === question.key ||
                      (needsText(chosenAnswer[question.key]) && !answerText[question.key])}
                    on:click={() => saveAnswer(question, chosenAnswer[question.key], answerText[question.key] || "")}
                  />
                </div>
              {/if}
            </div>
          {/each}
          {#each clarifications.answered as question (question.key)}
            <div class="pdd-question answered">
              <p class="pdd-question-title"><span class="pdd-question-step">{question.step}</span>{question.question}</p>
              <p class="pdd-question-given">
                ✓ {question.answer}{question.value ? `: ${question.value}` : ""}
                <button class="pdd-question-change" on:click={() => saveAnswer(question, "", "")}>Change</button>
              </p>
            </div>
          {/each}
        {/if}
      </div>
      <!-- Weitere Schritte zur Aufgabe: dezent im Inhalt statt als Knopfreihe im Fuss -->
      <div class="pdd-more">
        <strong>Also for this task</strong>
        <button type="button" class="pdd-link" on:click={downloadRpaBot} disabled={botLoading || !currentSubtask}>
          {botLoading ? "Building RPA bot…" : "Download RPA bot (PowerShell script)"}
        </button>
        <button type="button" class="pdd-link" on:click={campaign?.status === "laeuft" ? checkCampaign : startCampaign}
                disabled={campaignLoading || !currentSubtask}>
          {campaignLoading ? "Working…" : campaign?.status === "laeuft" ? "Check second recording round" : "Start second recording round"}
        </button>
        {#if campaignText}<span class="pdd-status-message">{campaignText}</span>{/if}
        {#if botError}<span class="pdd-status-message error" role="alert">{botError}</span>{/if}
        {#if campaignError}<span class="pdd-status-message error" role="alert">{campaignError}</span>{/if}
      </div>
  </div>
  <div slot="left-buttons" class="pdd-modal-actions">
    <Button
      text="Cancel"
      variant="secondary"
      on:click={() => (open = false)}
      disabled={pddGenerating}
    />
  </div>
</Modal>

<Modal
  bind:open={pddDownloadModalOpen}
  modalHeading={pddDownloadError
    ? "PDD generation failed"
    : pddDownloadCancelled
      ? "PDD generation cancelled"
      : generatedPddFilename
        ? "PDD ready"
        : `Generate ${pddDownloadGenerator === "n8n" ? "n8n" : "Alteryx"} PDD`}
  modalClass="pdd-modal-dialog"
  width={600}
  actions
  noSecondaryButton
  primaryButtonText={pddGenerating
    ? "Generating..."
    : generatedPddFilename
      ? "Download again"
      : pddDownloadError || pddDownloadCancelled
        ? "Try again"
        : "Generate PDD"}
  primaryButtonVariant="secondary"
  primaryButtonNotAllow={pddGenerating}
  primaryButtonAction={() => startPddDownload(pddDownloadGenerator)}
  preventCloseOnClickOutside
  on:close={closePddDownloadModal}
>
  <div class="pdd-template-copy n8n-prepare-copy">
    {#if generatedPddFilename}
      <p>
        <strong>{generatedPddFilename}</strong> was generated and downloaded.
      </p>
    {:else if pddDownloadError}
      <p class="pdd-status-message error" role="alert">
        {pddDownloadError}
      </p>
    {:else if pddDownloadCancelled}
      <p class="pdd-status-message">The PDD request was cancelled.</p>
    {:else}
      <p>
        The {pddDownloadGenerator === "n8n" ? "n8n" : "Alteryx"} PDD is
        generated from the currently visible task and BPMN graph. The PDF will
        download automatically when it is ready.
      </p>
    {/if}
    {#if pddGenerating}
      <div class="pdd-progress" role="status">
        <Loading small withOverlay={false} />
        <div>
          <strong>
            Generating PDD…
            {#if pddDownloadTotal}
              (step {pddDownloadStep} of {pddDownloadTotal})
            {/if}
          </strong>
          <span>
            {pddDownloadStepLabel} · {pddDownloadElapsedSeconds}s
          </span>
        </div>
      </div>
    {/if}
  </div>
  <div slot="left-buttons" class="pdd-modal-actions">
    <Button
      text={pddGenerating ? "Cancel request" : "Close"}
      variant="secondary"
      on:click={pddGenerating ? cancelPddDownload : closePddDownloadModal}
    />
  </div>
</Modal>

<Modal
  bind:open={n8nPrepareModalOpen}
  modalHeading={n8nPrepareError
    ? "n8n preparation failed"
    : n8nPrepareCancelled
      ? "n8n preparation cancelled"
      : preparedWorkflowName
        ? "n8n workflow ready"
        : "Prepare n8n workflow"}
  modalClass="pdd-modal-dialog"
  width={600}
  actions
  noSecondaryButton
  primaryButtonText={pddGenerating
    ? "Preparing..."
    : preparedWorkflowName
      ? "Open workflow in n8n"
      : n8nPrepareError || n8nPrepareCancelled
        ? "Try again"
        : "Prepare Workflow"}
  primaryButtonVariant="secondary"
  primaryButtonNotAllow={pddGenerating}
  primaryButtonAction={preparedWorkflowName ? openPreparedWorkflowInN8n : prepareN8nWorkflow}
  preventCloseOnClickOutside
  on:close={closeN8nPrepareModal}
>
  <div class="pdd-template-copy n8n-prepare-copy">
    {#if preparedWorkflowName}
      <p>
        <strong>{preparedWorkflowName}</strong> was created as an inactive n8n
        workflow draft. Review it in n8n before configuring credentials or
        activating it.
      </p>
    {:else if n8nPrepareError}
      <p class="pdd-status-message error" role="alert">
        {n8nPrepareError}
      </p>
    {:else if n8nPrepareCancelled}
      <p class="pdd-status-message">The n8n preparation request was cancelled.</p>
    {:else}
      <p>
        This creates an inactive n8n workflow draft from the selected task. It
        includes the PDD context, implementation guidance, and editable
        placeholders for the detected activities.
      </p>
      <p>
        No workflow will run automatically. Review the draft in n8n before
        configuring credentials or activating it.
      </p>
    {/if}
    {#if pddGenerating}
      <div class="pdd-progress" role="status">
        <Loading small withOverlay={false} />
        <div>
          <strong>
            Preparing workflow…
            {#if n8nPrepareTotal}
              (step {n8nPrepareStep} of {n8nPrepareTotal})
            {/if}
          </strong>
          <span>
            {n8nPrepareStepLabel} · {n8nPrepareElapsedSeconds}s
          </span>
        </div>
      </div>
    {/if}
  </div>
  <div slot="left-buttons" class="pdd-modal-actions">
    <Button
      text={pddGenerating
        ? "Cancel request"
        : preparedWorkflowName || n8nPrepareError || n8nPrepareCancelled
          ? "Close"
          : "Cancel"}
      variant="secondary"
      on:click={pddGenerating ? cancelN8nPreparation : closeN8nPrepareModal}
    />
  </div>
</Modal>

<div class="pdd-bpmn-renderer" aria-hidden="true">
  <BpmnInlinePreview bind:this={pddBpmnRendererElement} bpmnXml={pddBpmnXml} />
</div>

<style>
  :global(.pdd-modal-dialog .bx--modal-container) {
    width: min(40rem, calc(100vw - 2rem)) !important;
    max-width: 40rem !important;
  }

  /* Das PDD-Fenster mit den offenen Fragen braucht mehr Breite als die Fortschrittsfenster. */
  :global(.pdd-modal-dialog.pdd-modal-wide .bx--modal-container) {
    width: min(52rem, calc(100vw - 2rem)) !important;
    max-width: 52rem !important;
  }

  :global(.pdd-modal-dialog .bx--modal-content) {
    width: 100% !important;
    max-width: none !important;
    overflow-x: hidden;
  }

  :global(.pdd-modal-dialog .modal_wrapper) {
    width: 100% !important;
    box-sizing: border-box;
  }

  .pdd-v2-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 1rem;
  }

  .pdd-questions {
    border-top: 1px solid #e0e0e0;
    margin-top: 1.25rem;
    padding-top: 1rem;
  }

  .pdd-more {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1.25rem;
    border-top: 1px solid #e0e0e0;
    margin-top: 1.25rem;
    padding-top: 1rem;
  }

  .pdd-more strong,
  .pdd-more .pdd-status-message {
    width: 100%;
  }

  .pdd-link {
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    font-size: 0.875rem;
    text-decoration: underline;
    cursor: pointer;
  }

  .pdd-link:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .pdd-questions-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  .pdd-questions-count {
    background: #fff1e8;
    border-radius: 999px;
    color: #8a3800;
    font-size: 0.75rem;
    font-weight: 600;
    margin-left: 0.5rem;
    padding: 0.1rem 0.5rem;
  }

  .pdd-questions-hint {
    color: #525252;
    font-size: 0.875rem;
    margin: 0.25rem 0 0.75rem !important;
  }

  .pdd-question {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
    padding: 1rem 1.125rem;
  }

  .pdd-question.answered {
    background: #f6fbf7;
    border-color: #a7f0ba;
  }

  .pdd-question-title {
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.45;
  }

  .pdd-question-step {
    background: #f4f4f4;
    border-radius: 0.25rem;
    color: #525252;
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 600;
    margin-right: 0.5rem;
    padding: 0.1rem 0.4rem;
  }

  .pdd-question-answers {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .pdd-chip {
    background: #fff;
    border: 1px solid #c6c6c6;
    border-radius: 999px;
    color: #161616;
    cursor: pointer;
    font: inherit;
    font-size: 0.875rem;
    line-height: 1.2;
    padding: 0.45rem 0.9rem;
  }

  .pdd-chip:hover {
    border-color: #161616;
  }

  .pdd-chip.selected {
    background: #161616;
    border-color: #161616;
    color: #fff;
  }

  .pdd-question-save {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.75rem;
  }

  .pdd-question-text {
    border: 1px solid #c6c6c6;
    border-radius: 0.375rem;
    box-sizing: border-box;
    font: inherit;
    margin-top: 0.75rem;
    padding: 0.45rem 0.7rem;
    width: 100%;
  }

  .pdd-question-given {
    color: #24a148;
    font-size: 0.875rem;
    margin-top: 0.35rem !important;
  }

  .pdd-question-change {
    background: none;
    border: none;
    color: #0f62fe;
    cursor: pointer;
    font-size: 0.8rem;
    margin-left: 0.5rem;
    padding: 0;
    text-decoration: underline;
  }

  .pdd-template-copy {
    box-sizing: border-box;
    margin: 0;
    max-width: none;
    padding: 0;
    text-align: left;
    width: 100%;
  }

  .pdd-template-copy p {
    margin: 0;
    max-width: none; /* Fragen und Einleitung brachen vorher bei 60 % der Breite um */
    padding-right: 0;
    overflow-wrap: anywhere;
  }

  .n8n-prepare-copy {
    padding-right: 0;
  }

  .n8n-prepare-copy p + p {
    margin-top: 1rem;
  }

  .pdd-status-message {
    background: #f4f4f4;
    border-left: 0.25rem solid #8d8d8d;
    padding: 0.875rem 1rem;
  }

  .pdd-status-message.error {
    background: #fff1f1;
    border-left-color: #da1e28;
    color: #a2191f;
  }

  .pdd-progress {
    align-items: center;
    background: #f4f4f4;
    border-radius: var(--br);
    display: flex;
    gap: 0.75rem;
    box-sizing: border-box;
    margin: 1rem 0 0;
    padding: 0.875rem 1rem;
    width: 100%;
  }

  .pdd-progress strong,
  .pdd-progress span {
    display: block;
  }

  .pdd-progress span {
    color: #525252;
    font-size: 0.875rem;
    margin-top: 0.2rem;
  }

  .pdd-modal-actions {
    display: flex;
    gap: 0.5rem;
  }

  :global(.pdd-modal-dialog .buttons-container) {
    flex-wrap: wrap;
  }

  .pdd-bpmn-renderer {
    position: fixed;
    top: 0;
    left: -10000px;
    width: 1600px;
    height: 650px;
    overflow: hidden;
    pointer-events: none;
    opacity: 0;
  }
</style>
